// docs/47 — the spell effects' little pixel textures, drawn once into canvases: sparks, flames,
// flakes, shards, leaves, drops, motes and stars for the particles; a soft glow, a ring and a
// casting rune for the larger shapes. White, so every element tints them its own colour.
import Phaser from 'phaser';

export const TEX = {
  px: 'fk-sp-px', dot: 'fk-sp-dot', star: 'fk-sp-star', flame: 'fk-sp-flame', flake: 'fk-sp-flake', shard: 'fk-sp-shard',
  leaf: 'fk-sp-leaf', drop: 'fk-sp-drop', plus: 'fk-sp-plus', smoke: 'fk-sp-smoke', glow: 'fk-sp-glow', ring: 'fk-sp-ring',
  rune: 'fk-sp-rune', arrow: 'fk-sp-arrow', thorn: 'fk-sp-thorn', scorch: 'fk-sp-scorch', sigil: 'fk-sp-sigil', reticle: 'fk-sp-reticle', shield: 'fk-sp-shield',
  dagger: 'fk-sp-dagger',
  // docs/104: the bard's notes (♪ ♫ ♬), the paladin's blade, Encore's clock, the druid's tuft and stones
  note1: 'fk-sp-note1', note2: 'fk-sp-note2', note3: 'fk-sp-note3', blade: 'fk-sp-blade', clock: 'fk-sp-clock', tuft: 'fk-sp-tuft',
  pebble: 'fk-sp-pebble', streak: 'fk-sp-streak',
} as const;

/** docs/104 BD: the three note glyphs, in the order the songs cycle them. */
export const NOTES = [TEX.note1, TEX.note2, TEX.note3] as const;

type Ctx = CanvasRenderingContext2D;

function make(tm: Phaser.Textures.TextureManager, key: string, w: number, h: number, draw: (x: Ctx) => void, linear = false): void {
  if (tm.exists(key)) return;
  const c = tm.createCanvas(key, w, h)!;
  const x = c.getContext();
  x.clearRect(0, 0, w, h);
  draw(x);
  c.refresh();
  if (linear) c.setFilter(Phaser.Textures.FilterMode.LINEAR);
}

/** Pixel art by string rows: '#' full, '+' half, '.' faint, anything else empty. */
function pixels(rows: string[]): (x: Ctx) => void {
  return (x) => {
    rows.forEach((row, y) => [...row].forEach((ch, i) => {
      const a = ch === '#' ? 1 : ch === '+' ? 0.6 : ch === '.' ? 0.3 : 0;
      if (a) { x.fillStyle = `rgba(255,255,255,${a})`; x.fillRect(i, y, 1, 1); }
    }));
  };
}

export function ensureSpellTextures(scene: Phaser.Scene): void {
  const tm = scene.textures;
  make(tm, TEX.px, 2, 2, (x) => { x.fillStyle = '#fff'; x.fillRect(0, 0, 2, 2); });
  make(tm, TEX.dot, 3, 3, pixels(['+#+', '###', '+#+']));
  make(tm, TEX.star, 5, 5, pixels(['  #  ', '  #  ', '##### ', '  #  ', '  #  ']));
  make(tm, TEX.flame, 5, 7, pixels(['  .  ', ' .+  ', ' +#+ ', '+###+', '#####', '+###+', ' +#+ ']));
  make(tm, TEX.flake, 5, 5, pixels(['+ # +', ' +#+ ', '#####', ' +#+ ', '+ # +']));
  make(tm, TEX.shard, 3, 7, pixels([' # ', ' # ', '+#+', '###', '###', '+#+', ' + ']));
  make(tm, TEX.leaf, 5, 3, pixels([' ++# ', '+###+', '#++  ']));
  make(tm, TEX.drop, 3, 4, pixels([' + ', '+#+', '###', '+#+']));
  make(tm, TEX.plus, 5, 5, pixels(['  #  ', '  #  ', '#####', '  #  ', '  #  ']));
  make(tm, TEX.arrow, 3, 9, pixels([' # ', '###', ' # ', ' # ', ' # ', ' # ', ' # ', '+ +', '# #']));
  // docs/103: a rogue's dagger, point up (rotated along its flight like the arrow)
  make(tm, TEX.dagger, 5, 11, pixels(['  #  ', ' +#+ ', ' ### ', ' ### ', ' ### ', ' ### ', '#####', ' +#+ ', '  #  ', '  #  ', ' +#+ ']));
  // docs/104 BD: musical notes as pixel glyphs — ♪ an eighth, ♫ two beamed, ♬ two double-beamed;
  // a dark rim is drawn round each by the caller's shade copy, so they are plain white here
  make(tm, TEX.note1, 6, 8, pixels(['  ##  ', '  #+# ', '  #  #', '  #  +', '  #   ', '+##   ', '###   ', '+#+   ']));
  make(tm, TEX.note2, 7, 8, pixels(['  #####', '  #+++#', '  #   #', '  #   #', '  #   #', '+## +##', '### ###', '+#+ +#+']));
  make(tm, TEX.note3, 7, 8, pixels(['  #####', '  #   #', '  #####', '  #   #', '  #   #', '+## +##', '### ###', '+#+ +#+']));
  // the paladin's blade, point down: a broad bright sword with a cross-guard
  make(tm, TEX.blade, 9, 22, pixels([
    '   +#+   ', '   ###   ', '    #    ', '+#######+', '#########', '   ###   ', '   #+#   ', '   #+#   ', '   #+#   ', '   #+#   ', '   #+#   ',
    '   #+#   ', '   #+#   ', '   #+#   ', '   #+#   ', '   #+#   ', '   #+#   ', '   #+#   ', '   ###   ', '   ###   ', '    #    ', '    +    ']));
  // Encore's clock: a round face, its twelve, three, six and nine (the hands are drawn live)
  make(tm, TEX.clock, 11, 11, pixels(['   #####   ', '  #  #  #  ', ' #       # ', '#         #', '#         #', '##   +   ##', '#         #', '#         #', ' #       # ', '  #  #  #  ', '   #####   ']));
  // a thicket's tuft: a little bush of three leaves on a stem
  make(tm, TEX.tuft, 9, 8, pixels(['   +#+   ', ' +###+#+ ', '+##+####+', '###+#+###', '+#+ # +#+', '    #    ', '   +#+   ', '  +###+  ']));
  make(tm, TEX.pebble, 4, 3, pixels(['+##+', '####', '+##+']));
  // a wind streak: a long thin line, thick in the middle
  make(tm, TEX.streak, 14, 2, pixels(['..++######++..', '...+++##+++...']));
  make(tm, TEX.thorn, 3, 6, pixels([' # ', ' # ', '+#+', ' # ', ' + ', ' . ']));
  make(tm, TEX.smoke, 16, 16, (x) => {
    const g = x.createRadialGradient(8, 8, 1, 8, 8, 8);
    g.addColorStop(0, 'rgba(255,255,255,0.9)'); g.addColorStop(0.6, 'rgba(255,255,255,0.45)'); g.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = g; x.fillRect(0, 0, 16, 16);
  }, true);
  make(tm, TEX.glow, 64, 64, (x) => {
    const g = x.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.25, 'rgba(255,255,255,0.55)'); g.addColorStop(0.6, 'rgba(255,255,255,0.14)'); g.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = g; x.fillRect(0, 0, 64, 64);
  }, true);
  make(tm, TEX.scorch, 32, 32, (x) => {
    const g = x.createRadialGradient(16, 16, 0, 16, 16, 16);
    g.addColorStop(0, 'rgba(255,255,255,0.85)'); g.addColorStop(0.7, 'rgba(255,255,255,0.5)'); g.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = g; x.fillRect(0, 0, 32, 32);
  }, true);
  make(tm, TEX.ring, 48, 48, (x) => {
    x.strokeStyle = '#fff'; x.lineWidth = 3; x.beginPath(); x.arc(24, 24, 21, 0, Math.PI * 2); x.stroke();
    x.strokeStyle = 'rgba(255,255,255,0.4)'; x.lineWidth = 2; x.beginPath(); x.arc(24, 24, 17, 0, Math.PI * 2); x.stroke();
  }, true);
  // a casting circle: two rings, eight ticks, a star of two triangles
  make(tm, TEX.rune, 64, 64, (x) => {
    x.strokeStyle = '#fff'; x.lineWidth = 2;
    x.beginPath(); x.arc(32, 32, 29, 0, Math.PI * 2); x.stroke();
    x.lineWidth = 1; x.beginPath(); x.arc(32, 32, 23, 0, Math.PI * 2); x.stroke();
    for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; x.beginPath(); x.moveTo(32 + Math.cos(a) * 23, 32 + Math.sin(a) * 23); x.lineTo(32 + Math.cos(a) * 29, 32 + Math.sin(a) * 29); x.stroke(); }
    for (const off of [0, Math.PI]) {
      x.beginPath();
      for (let i = 0; i <= 3; i++) { const a = off - Math.PI / 2 + (i / 3) * Math.PI * 2; const px = 32 + Math.cos(a) * 21, py = 32 + Math.sin(a) * 21; if (i) x.lineTo(px, py); else x.moveTo(px, py); }
      x.stroke();
    }
    for (let i = 0; i < 8; i++) { const a = ((i + 0.5) / 8) * Math.PI * 2; x.fillStyle = '#fff'; x.fillRect(Math.round(32 + Math.cos(a) * 26) - 1, Math.round(32 + Math.sin(a) * 26) - 1, 2, 2); }
  }, true);
  // a curse's sigil: an eye in a broken ring
  make(tm, TEX.sigil, 48, 48, (x) => {
    x.strokeStyle = '#fff'; x.lineWidth = 2;
    for (let i = 0; i < 6; i++) { const a0 = (i / 6) * Math.PI * 2 + 0.12, a1 = a0 + Math.PI / 3 - 0.24; x.beginPath(); x.arc(24, 24, 21, a0, a1); x.stroke(); }
    x.beginPath(); x.ellipse(24, 24, 12, 6, 0, 0, Math.PI * 2); x.stroke();
    x.fillStyle = '#fff'; x.beginPath(); x.arc(24, 24, 3, 0, Math.PI * 2); x.fill();
  }, true);
  make(tm, TEX.reticle, 48, 48, (x) => {
    x.strokeStyle = '#fff'; x.lineWidth = 2;
    x.beginPath(); x.arc(24, 24, 16, 0, Math.PI * 2); x.stroke();
    for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]] as const) { x.beginPath(); x.moveTo(24 + dx * 10, 24 + dy * 10); x.lineTo(24 + dx * 23, 24 + dy * 23); x.stroke(); }
    x.fillStyle = '#fff'; x.fillRect(23, 23, 2, 2);
  }, true);
  make(tm, TEX.shield, 24, 28, (x) => {
    x.strokeStyle = '#fff'; x.lineWidth = 2;
    x.beginPath(); x.moveTo(12, 2); x.lineTo(22, 6); x.lineTo(21, 15); x.quadraticCurveTo(18, 23, 12, 26); x.quadraticCurveTo(6, 23, 3, 15); x.lineTo(2, 6); x.closePath(); x.stroke();
    x.globalAlpha = 0.25; x.fillStyle = '#fff'; x.fill();
  }, true);
}
